/**
 * =============================================================================
 * CONSENT RECORD INSPECTION (BL-PRIVACY-CONSENT-CAPTURE-001)
 * =============================================================================
 *
 * Minimal, read-only operator support panel: enter an EXACT question id to see
 * the public-display consent records captured at submission for that question
 * and its replies (policy version / consent copy key / timestamp / locale, and
 * whether an admin-only IP was captured). Supports "what did this person
 * consent to, and when?" support / DSAR questions without raw D1 access.
 *
 * Scope:
 *   - Read-only inspection of an EXPLICIT question scope (the operator names
 *     the question id). NO browse / list / search / enumeration, NO export.
 *   - Returns consent fields only — never the raw IP (only its availability),
 *     never email, never question/reply free text.
 *   - Every inspection is audit-logged server-side; the panel says so.
 * =============================================================================
 */

import React, { useCallback, useState } from 'react';
import { Box, Button, Flex, FlexItem, Form, FormGroup, Input, Panel, Table, Text } from '@bigcommerce/big-design';
import type { ConsentInspectionRecord, ConsentInspectionResponse } from '@find-my-part/types';
import { adminApi } from '../../api/client';
import { useSession } from '../../hooks/useSession';
import { StatusChip } from '../common/StatusChip';

/** Render a yes/no consent badge. */
function ConsentBadge({ present }: { present: boolean }): React.ReactElement {
  return <StatusChip label={present ? 'Consent on record' : 'No record'} variant={present ? 'success' : 'secondary'} />;
}

/**
 * Admin panel for the read-only consent-record inspection.
 */
export function ConsentInspection(): React.ReactElement | null {
  const { token, isAuthenticated } = useSession();

  const [questionId, setQuestionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ConsentInspectionResponse | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!token) return;

      const trimmed = questionId.trim();
      if (!trimmed) {
        setError('Enter a question id to inspect.');
        setResult(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const res = await adminApi.support.consentInspection(token, trimmed);
        if (res.success) {
          setResult(res.data);
        } else {
          setResult(null);
          setError(res.error?.message ?? 'Inspection failed. Please try again.');
        }
      } catch {
        setResult(null);
        setError('Inspection failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [token, questionId]
  );

  // Not authenticated — nothing to control (parent handles the login UX).
  if (!isAuthenticated || !token) {
    return null;
  }

  return (
    <Panel header="Consent record inspection" data-testid="consent-inspection-card">
      <Text color="secondary" marginBottom="small">
        Inspect the public-display consent captured at submission for a specific question and its
        replies. Enter the exact question id (for example, &ldquo;q_abc123def456&rdquo;). This shows
        consent details only — not the question text, replies, or email addresses.
      </Text>

      <Box
        backgroundColor="secondary10"
        borderRadius="normal"
        padding="small"
        marginBottom="medium"
        data-testid="consent-inspection-audit-notice"
      >
        <Text marginBottom="none" color="secondary">
          For privacy, every inspection is <strong>audit-logged</strong>. The submitter&apos;s IP
          address is never shown — only whether one was recorded.
        </Text>
      </Box>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Input
            label="Question id"
            placeholder="q_abc123def456"
            value={questionId}
            onChange={(e) => setQuestionId(e.target.value)}
            data-testid="consent-inspection-question-id"
          />
        </FormGroup>
        <Flex>
          <FlexItem>
            <Button type="submit" disabled={loading} isLoading={loading} data-testid="consent-inspection-submit">
              Inspect
            </Button>
          </FlexItem>
        </Flex>
      </Form>

      {error && (
        <Box marginTop="medium">
          <Text color="danger" data-testid="consent-inspection-error">
            {error}
          </Text>
        </Box>
      )}

      {result && !result.questionFound && (
        <Box marginTop="medium" data-testid="consent-inspection-not-found">
          <Text color="secondary">
            No question found for this id in this store. Check the id and try again.
          </Text>
        </Box>
      )}

      {result && result.questionFound && (
        <Box marginTop="medium" data-testid="consent-inspection-result">
          {/* ADR-025 A-H (M13): the table scrolls INSIDE this container — the
              page body must never scroll sideways at supported widths. */}
          <Box data-testid="consent-table-scroll" style={{ overflowX: 'auto' }}>
            <Table
              columns={[
                {
                  header: 'Entity',
                  hash: 'entity',
                  render: (r: ConsentInspectionRecord) => (
                    <Flex flexDirection="column" flexGap="2px">
                      <Text bold marginBottom="none">
                        {r.entityType === 'QUESTION' ? 'Question' : 'Reply'}
                      </Text>
                      <Text color="secondary" marginBottom="none">
                        {r.entityId}
                      </Text>
                    </Flex>
                  ),
                },
                {
                  header: 'Consent',
                  hash: 'consent',
                  render: (r: ConsentInspectionRecord) => <ConsentBadge present={r.consentPresent} />,
                },
                {
                  header: 'Version',
                  hash: 'version',
                  render: (r: ConsentInspectionRecord) => <Text marginBottom="none">{r.version ?? '—'}</Text>,
                },
                {
                  header: 'Consent copy',
                  hash: 'textKey',
                  render: (r: ConsentInspectionRecord) => <Text marginBottom="none">{r.textKey ?? '—'}</Text>,
                },
                {
                  header: 'Granted at',
                  hash: 'grantedAt',
                  render: (r: ConsentInspectionRecord) => <Text marginBottom="none">{r.grantedAt ?? '—'}</Text>,
                },
                {
                  header: 'Locale',
                  hash: 'locale',
                  render: (r: ConsentInspectionRecord) => <Text marginBottom="none">{r.locale ?? '—'}</Text>,
                },
                {
                  header: 'IP captured',
                  hash: 'ipAvailable',
                  render: (r: ConsentInspectionRecord) => (
                    <Text marginBottom="none">{r.ipAvailable ? 'Yes' : 'No'}</Text>
                  ),
                },
              ]}
              items={result.records.map((r, index) => ({ ...r, id: `${r.entityType}-${r.entityId}-${index}` }))}
            />
          </Box>
        </Box>
      )}
    </Panel>
  );
}
